/**
 * 创作页右侧布局：**两个槽位**（左侧 AI + 右侧数据页），各自独立开合。
 *
 * ## 状态模型（第六版：两槽位）
 *
 * 前几版的教训：
 * 1. `open: Record<PaneId, boolean>` —— 点图标加/减一块面板；
 * 2. `active + pinned` —— 换页会把上一页关掉；
 * 3. VS Code 式「标签 + 多列 + 分页」—— 对只有四个功能页的写作工具过重；
 * 4. **单页**（`activePane` 一个字段）—— 一次只能开一页：打开 AI 时再点「资料库」，
 *    AI 就被挤掉了。用户要的是**两页并存**（AI 常驻左边、数据页在右边），于是有了这一版。
 *
 * 现在：
 * - `aiOpen`：AI 创作助手是否展开。它**永远在数据页左侧**；
 * - `dataPane`：右侧数据页（三个之一，或 `null`）。点不同的按钮**原位切换内容**，
 *   不再是"多标签"—— 标签条在本项目里从来没被用户接受过（第四版就删掉了）；
 * - `paneWidth`：功能区宽度，**所有页共用**（调一次处处生效）。
 *
 * 视觉上就是**从右向左连续伸出**：先开数据页，它从窗口右缘向左长出；再开 AI，
 * AI 从"已打开的数据页左边"继续向左长出（编辑区被挤窄，窗口宽度不变）。
 *
 * ## 两端的差别只在"数据页放哪"
 *
 * - **网页端**：数据页就在窗口内（AI 左边、数据页右边，见 `Editor/index.tsx` 的渲染顺序）；
 * - **桌面端**：数据页进**吸附窗口**（贴在主窗口左右侧的独立窗口），窗口内只有 AI。
 *   两边共用同一个 `dataPane` 字段 —— 它表示"当前打开的是哪个数据页"，
 *   至于渲染在窗口里还是吸附窗口里，由调用方按宿主决定（`dataInWindow`）。
 *
 * ## AI 创作助手**只隐藏、从不卸载**
 *
 * `AIAssistantPanel` 的回合状态（活动流、`isGenerating`、编辑器锁）都在组件内部，
 * 卸载即全丢。所以它一旦打开过就一直挂载，收起只是把宽度动画到 0
 * （`Editor/index.tsx` 用 key 固定它的 DOM 身份）。数据页是数据视图，关掉就是真关掉。
 *
 * 宽度持久化到 **localStorage**（用户要求"存在浏览器缓存中"），**不设上限**；
 * 但**"当前开着哪一页"不落盘** —— 每次打开软件，右侧都是收起的（用户明确要求）。
 */

/** 四个数据页 */
export type DataPaneId = 'settings' | 'data' | 'threads' | 'resident';

/** 右侧功能页（四个数据页 + AI 助手） */
export type PaneId = DataPaneId | 'ai';

/** 数据页的固定顺序 */
export const DATA_PANES: readonly DataPaneId[] = ['settings', 'data', 'threads', 'resident'];

/** 图标栏顺序：四个数据页 + AI（AI 放最后，与"它在最左侧展开"的直觉不冲突） */
export const PANE_ORDER: readonly PaneId[] = [...DATA_PANES, 'ai'];

export const PANE_LABEL: Record<PaneId, string> = {
  settings: '设定管理',
  data: '资料库',
  threads: '伏笔追踪',
  resident: '余白',
  ai: 'AI 创作助手',
};

/**
 * AI 助手这一页。
 *
 * 它可以在**独立窗口**里打开吗？**不可以** —— 副窗口是独立的 SPA 实例，
 * 拿不到主窗口里那个正在跑的回合，会出现"两个 AI 面板各说各话"。
 * 所以吸附窗口只给另外三页。
 */
export const AI_PANE = 'ai';

/**
 * 宽度默认值的计算（**纯函数**，便于单测）——用户约定的那套规则：
 *
 * 首次打开软件时算一次，使「打开了 AI 创作助手的主窗体」+「吸附窗口」**略小于屏幕宽度**；
 * 之后用户调过哪一项就记哪一项，下次直接用记录值；设置页里可以一键重算。
 *
 * 三项各自的定法与理由：
 * - **吸附窗口**：屏幕的 18%，夹在 260~520 —— 这是"三页数据面板"的舒适宽度区间；
 * - **AI 面板**：按**屏幕档位**给（见 `TIER_WIDTHS`）—— 14 寸笔记本上"20% 的绝对像素"
 *   会把编辑区挤得没法写作，所以要随档位收窄；
 * - **主窗体**：`屏幕 − 吸附窗口 − 边距`。边距取屏幕 2%（40~120），
 *   留出这点余量，"略小于屏幕"才真的有余量（也能让右侧吸附窗口不被任务栏/边缘吃掉）。
 *
 * 全是 **CSS 像素**（与前端一致）：Rust 侧负责乘缩放比换算成物理像素。
 *
 * ⚠️ 两个宽度活在**两套坐标系**里，别混：
 *   - `main` / `satellite` 是**原生窗口尺寸**（屏幕 CSS 像素）⇒ 用 `screenWidth` 算；
 *   - `ai` / `sidebar` 是**布局宽度**（`#root` 里有 zoom，见 utils/uiScale.ts）
 *     ⇒ 用 `tierBasisWidth`（布局空间宽度）算。
 * 开了"随窗口自动缩放"时，布局空间恒为设计尺寸，于是各档默认值在**任何屏幕上都一致**
 * （这正是"窗体比例相同则版面相同"）；缩放关闭时两者相同，退化为按屏幕分档。
 */
export function computeDefaultWidths(
  screenWidth: number,
  tierBasisWidth: number = screenWidth,
): {
  main: number; ai: number; satellite: number; sidebar: number;
} {
  const screen = Number.isFinite(screenWidth) && screenWidth > 0 ? screenWidth : 1440;
  const satellite = Math.round(Math.min(520, Math.max(260, screen * 0.18)));
  const margin = Math.round(Math.min(120, Math.max(40, screen * 0.02)));
  const main = Math.max(640, screen - satellite - margin);
  const tier = TIER_WIDTHS[screenTier(tierBasisWidth)];
  return { main, ai: tier.pane, satellite, sidebar: tier.sidebar };
}

/**
 * 屏幕档位 —— 首次启动的默认宽度、以及"挤不下时先压谁"都按它来。
 *
 * 阈值照着真实设备定：
 * - `wide`  ≥1600：外接大屏 / 全屏 1920；
 * - `normal` 1360~1599：1920@125%（1536）、14 寸 1440 笔记本；
 * - `compact` 1100~1359：1920@150%（1280）、13 寸高分屏 —— **用户实测最挤的一档**；
 * - `narrow` <1100：半屏窗口、平板竖屏。
 */
export type ScreenTier = 'wide' | 'normal' | 'compact' | 'narrow';

export function screenTier(width: number): ScreenTier {
  const w = Number.isFinite(width) && width > 0 ? width : 1440;
  if (w >= 1600) return 'wide';
  if (w >= 1360) return 'normal';
  if (w >= 1100) return 'compact';
  return 'narrow';
}

/** 每一档的默认宽度。窄档直接落在两个下限上（侧栏 160 / 面板 240）。 */
export const TIER_WIDTHS: Record<ScreenTier, { sidebar: number; pane: number }> = {
  wide: { sidebar: 260, pane: 380 },
  normal: { sidebar: 240, pane: 340 },
  compact: { sidebar: 200, pane: 300 },
  narrow: { sidebar: 160, pane: 240 },
};

/** 图标栏宽度（与 editor.css 的 `.pane-rail` 一致）——算"还剩多少给编辑区"要减掉它。 */
export const RAIL_PX = 56;

/**
 * 编辑区的最小可用宽度：压缩侧栏/面板时**优先保它**。
 *
 * 取值是"够写作"与"给面板留余地"之间的取舍。**500 → 360**（用户反馈"面板只能缩小、不能扩大，
 * 上限大概就是默认宽度"）：两块面板同时在窗口内时，面板的上限 = (布局宽 − 侧栏 − 图标栏 − 这个值) / 块数 ——
 * 每往下调 100px，两块面板就各能多宽 50px。360 大约是一行十几个汉字的量级，
 * 已经不是舒适值但可读；真嫌窄就关掉一块面板，或**还原窗口**（数据页回副窗口，窗口内就只剩一块）。
 *
 * 真挤不到时（窗口极窄）就把侧栏与面板都压到下限，剩下的归编辑区。
 */
export const MIN_EDITOR_PX = 360;

export interface PaneLayout {
  /** 左侧栏宽度（px） */
  sidebar: number;
  /** AI 创作助手是否展开。展开时它位于**数据页左侧**（没有数据页时就是最右侧那一块） */
  aiOpen: boolean;
  /** 当前打开的数据页；`null` = 没开。点不同按钮**原位切换**（不重建、不重放动画） */
  dataPane: DataPaneId | null;
  /**
   * 主窗体宽度（CSS 像素）。
   *
   * 首次打开时由 `computeDefaultWidths` 算出来并应用；之后用户拖窗口改变它，
   * 这里就记下新值（下次打开直接用）。设置页的「重置页面宽度」会重算这一项。
   */
  mainWidth: number;
  /** 吸附窗口宽度（CSS 像素）。规则同 `mainWidth`。 */
  satelliteWidth: number;
  /** 宽度默认值是否已经算过（首次启动算一次，之后不再自动改用户调好的值） */
  widthsReady: boolean;
  /**
   * 右侧功能区宽度（px）—— **所有功能页共用同一个宽度**。
   *
   * 曾经是"每页各记一个宽度"，但用户实际用起来会觉得别扭：调好「资料库」的宽度，
   * 切到「伏笔追踪」又要重调一次。功能区就一条宽度，调一次处处生效。
   */
  paneWidth: number;
}

/**
 * 面板最小宽度：窄于此值内容（筛选行、卡片）会挤到不可用。
 * 这是**下限**，不是上限 —— 上限刻意为无。
 */
export const MIN_PANE_PX = 240;
export const MIN_SIDEBAR_PX = 160;

/** 键盘调整宽度的步长（Shift 加大步长） */
export const RESIZE_STEP_PX = 16;
export const RESIZE_STEP_LARGE_PX = 64;

/** 右侧功能区的默认宽度（所有功能页共用） */
export const DEFAULT_PANE_WIDTH = 380;

/** 吸附窗口的宽度下限（与 Rust 的 SAT_MIN_WIDTH 对齐） */
export const SAT_MIN_WIDTH_FALLBACK = 260;

/** 面板展开/收起的动画时长（毫秒）—— 与 CSS 里的 `transition: width` 保持一致 */
export const PANE_ANIM_MS = 190;

/** 存储键 —— 模型换代了，换键名让旧数据自然失效（而不是被清洗成半对的状态） */
export const LAYOUT_STORAGE_KEY = 'xiaotianshu:editor-pane-layout:v4';

export const isDataPaneId = (v: unknown): v is DataPaneId =>
  typeof v === 'string' && (DATA_PANES as readonly string[]).includes(v);

export const isPaneId = (v: unknown): v is PaneId =>
  typeof v === 'string' && (PANE_ORDER as readonly string[]).includes(v);

/** 默认布局：**右侧什么都不开**（只有图标栏）。 */
export function defaultLayout(): PaneLayout {
  return {
    sidebar: 260,
    aiOpen: false,
    dataPane: null,
    paneWidth: DEFAULT_PANE_WIDTH,
    // 这三个由"首次启动计算"填上（见 computeDefaultWidths）；
    // 在算出来之前先用一组保守值，避免出现 0 宽度的窗口。
    mainWidth: 1200,
    satelliteWidth: 420,
    widthsReady: false,
  };
}

/**
 * 把一个宽度值清洗成可用值。
 *
 * 规则（两类的处理**刻意不同**）：
 * - **非数值**（`null` / `undefined` / `''` / `'abc'` / `NaN` / 对象）→ 视为数据损坏，退回默认值。
 * - **是数值**（含 `0` 与负数这类退化值）→ 说明"曾经有人在设宽度"，
 *   于是**抬到下限**而不是跳回默认：下限本身随版本变过，
 *   抬到下限能尽量保住用户原有的相对布局，跳回默认则会把它重置掉。
 *
 * **不设上限**（用户明确要求允许把面板拖到任意宽）。
 */
function sanitizeWidth(value: unknown, fallback: number, min: number): number {
  const n = typeof value === 'number'
    ? value
    : (typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.round(n));
}

/**
 * 清洗从存储读回的对象：任何字段缺失/类型不对/越界都退回默认值。**永不抛异常**。
 */
export function sanitizeLayout(raw: unknown): PaneLayout {
  const d = defaultLayout();
  if (!raw || typeof raw !== 'object') return d;

  const r = raw as {
    sidebar?: unknown; aiOpen?: unknown; dataPane?: unknown; paneWidth?: unknown;
    mainWidth?: unknown; satelliteWidth?: unknown; widthsReady?: unknown;
  };

  return {
    sidebar: sanitizeWidth(r.sidebar, d.sidebar, MIN_SIDEBAR_PX),
    mainWidth: sanitizeWidth(r.mainWidth, d.mainWidth, 480),
    satelliteWidth: sanitizeWidth(r.satelliteWidth, d.satelliteWidth, SAT_MIN_WIDTH_FALLBACK),
    widthsReady: r.widthsReady === true,
    aiOpen: r.aiOpen === true,
    // 认不出来的 pane id（含旧版本的字段）一律当作"没开"
    dataPane: isDataPaneId(r.dataPane) ? r.dataPane : null,
    paneWidth: sanitizeWidth(r.paneWidth, d.paneWidth, MIN_PANE_PX),
  };
}

/** 只用于测试注入；浏览器里取 localStorage（隐私模式/被禁用时访问会抛，故包 try） */
export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function safeStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

/**
 * 落盘的内容：**只存偏好（宽度），不存"当前打开的是哪一页"**。
 *
 * 用户明确要求："如果软件关闭时右侧有侧栏打开，再次打开软件时……我需要软件打开时默认侧边栏关闭。"
 * 也就是说"开着哪一页"属于会话状态，不该跨启动保留；而宽度是用户调出来的偏好，应当保留。
 */
function persistedOf(layout: PaneLayout): {
  sidebar: number; paneWidth: number; mainWidth: number; satelliteWidth: number; widthsReady: boolean;
} {
  return {
    sidebar: layout.sidebar,
    paneWidth: layout.paneWidth,
    // 宽度配置也要落盘：下次打开直接用用户调好的值（用户约定）
    mainWidth: layout.mainWidth,
    satelliteWidth: layout.satelliteWidth,
    widthsReady: layout.widthsReady,
  };
}

export function loadLayout(storage: StorageLike | null = safeStorage()): PaneLayout {
  if (!storage) return defaultLayout();
  try {
    const raw = storage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return defaultLayout();
    // 读回来的当前页一律丢弃：启动时右侧总是收起的
    return { ...sanitizeLayout(JSON.parse(raw)), aiOpen: false, dataPane: null };
  } catch {
    // JSON 损坏（历史版本/手工改动）时退回默认，而不是把异常抛到渲染树里
    return defaultLayout();
  }
}

export function saveLayout(layout: PaneLayout, storage: StorageLike | null = safeStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(persistedOf(layout)));
  } catch {
    // 配额满 / 隐私模式：静默降级（布局偏好丢了不影响写作）
  }
}

export const clampPaneWidth = (px: number): number =>
  Number.isFinite(px) ? Math.max(MIN_PANE_PX, Math.round(px)) : MIN_PANE_PX;

export const clampSidebarWidth = (px: number): number =>
  Number.isFinite(px) ? Math.max(MIN_SIDEBAR_PX, Math.round(px)) : MIN_SIDEBAR_PX;

// ── 状态变换（纯函数，便于单测）────────────────────────────────────────

/**
 * 点图标栏：**打开 / 收起**（两个槽位互不影响）。
 *
 * - AI → 切换 `aiOpen`；
 * - 数据页 → 已经是它则收起；是别的一页则**原位换内容**（`dataPane` 直接改，
 *   动画不会重放 —— 用户要的正是"在当前面板直接切换"）；
 * - 两个槽位因此可以同时开着：AI 在左、数据页在右。
 */
export function togglePane(layout: PaneLayout, id: PaneId): PaneLayout {
  if (id === AI_PANE) return { ...layout, aiOpen: !layout.aiOpen };
  return { ...layout, dataPane: layout.dataPane === id ? null : id };
}

/** 直接设置数据页（吸附窗口关闭/切页、全局搜索跳转等用它） */
export function setDataPane(layout: PaneLayout, pane: DataPaneId | null): PaneLayout {
  return layout.dataPane === pane ? layout : { ...layout, dataPane: pane };
}

/** 直接设置 AI 槽位（快捷键、程序化开关用它） */
export function setAiOpen(layout: PaneLayout, open: boolean): PaneLayout {
  return layout.aiOpen === open ? layout : { ...layout, aiOpen: open };
}

/**
 * **把某页显示出来**（全局搜索跳转、"在新窗口打开"失败回滚等场景）。
 *
 * 语义是"确保它开着"，**不切换**语义：不会把另外一页关掉 —— 两槽位并存正是这一版的目的。
 */
export function showPane(layout: PaneLayout, id: PaneId): PaneLayout {
  if (id === AI_PANE) return setAiOpen(layout, true);
  return setDataPane(layout, id);
}

/** 收起右侧两页（离开创作页、或用户要求"全部收起"时用） */
export function closeAllPanes(layout: PaneLayout): PaneLayout {
  if (!layout.aiOpen && layout.dataPane === null) return layout;
  return { ...layout, aiOpen: false, dataPane: null };
}

/** AI 槽位占的宽度（收起时是 0 —— 动画的起始值要的就是 0） */
export function aiColumnWidth(layout: PaneLayout): number {
  return layout.aiOpen ? layout.paneWidth : 0;
}

/** 数据页占的宽度（`inWindow` 为假时它不在窗口内 —— 桌面端在吸附窗口里，占 0） */
export function dataColumnWidth(layout: PaneLayout, inWindow: boolean): number {
  return inWindow && layout.dataPane ? layout.paneWidth : 0;
}

/**
 * 窗口内右侧功能区**总宽度**（AI + 数据页）。
 *
 * 用途是"编辑区被占掉多少"这类判断（例如是否需要给窗口加宽的保护性检查）——
 * 不是渲染宽度：渲染时两个槽位各拿各的值，才能各自独立做入场动画。
 */
export function inWindowColumnWidth(layout: PaneLayout, dataInWindow: boolean): number {
  return aiColumnWidth(layout) + dataColumnWidth(layout, dataInWindow);
}

/** 记下主窗体宽度（用户拖动窗口后调用） */
export function setMainWidth(layout: PaneLayout, px: number): PaneLayout {
  const width = Math.max(480, Math.round(px));
  return layout.mainWidth === width ? layout : { ...layout, mainWidth: width };
}

/** 记下吸附窗口宽度（用户拖动它之后调用） */
export function setSatelliteWidth(layout: PaneLayout, px: number): PaneLayout {
  const width = Math.max(SAT_MIN_WIDTH_FALLBACK, Math.round(px));
  return layout.satelliteWidth === width ? layout : { ...layout, satelliteWidth: width };
}

/**
 * 只更新"副窗口宽度"这一项（给**吸附窗口自己**用）。
 *
 * 副窗口是独立的 SPA 实例，但同源 → 与主窗口共享同一个 localStorage。
 * 所以它在自己那侧量到宽度后可以直接写回来，主窗口下次打开就按这个宽度摆
 * （用户约定："用户调整了页面宽度则记录下来"）。
 *
 * 做法是"读—改—写"整份布局：只改一个字段，其余字段原样带回，
 * 这样不会把主窗口刚调的侧栏/面板宽度抹掉。
 */
export function saveSatelliteWidth(px: number, storage: StorageLike | null = safeStorage()): void {
  if (!storage) return;
  const next = setSatelliteWidth(loadLayout(storage), px);
  saveLayout(next, storage);
}

export function applyDefaultWidths(
  layout: PaneLayout,
  screenWidth: number,
  tierBasisWidth: number = screenWidth,
): PaneLayout {
  const { main, ai, satellite, sidebar } = computeDefaultWidths(screenWidth, tierBasisWidth);
  return {
    ...layout,
    mainWidth: main,
    satelliteWidth: satellite,
    paneWidth: clampPaneWidth(ai),
    sidebar: clampSidebarWidth(sidebar),
    widthsReady: true,
  };
}

// ── 按当前视口算"有效宽度"（不改用户偏好）──────────────────────────────

/**
 * 当前视口下**实际参与布局**的侧栏与面板宽度。
 *
 * ## 为什么要有这一层（用户实测反馈）
 *
 * 网页端一直是大屏/全屏用的，转到桌面端或 14 寸笔记本后窗口小了很多，而宽度是**持久化的
 * 用户偏好** —— 一开右侧功能页，`侧栏 + 面板` 就把编辑区挤得没法写作（尤其是同时开着
 * AI 与数据页：两块各 380 就是 760）。
 *
 * 处理方式刻意**不覆盖用户偏好**，而是像 CSS 里既有的 `--sidebar-effective` 一样：
 * 存储值原样保留，渲染时按当前视口**往下压**（压到下限为止），窗口变大自动恢复原值。
 * 于是"拖到很宽的面板"在小窗口里不会毁掉编辑区，换回大屏又完好如初。
 *
 * 压缩顺序（先保编辑区 `MIN_EDITOR_PX`）：
 *   1. 侧栏先按"不超过视口 32%"封顶（与 CSS 的窄屏规则同一套）；
 *   2. 面板整体放不下时，按"均分剩余空间"压窄，但不低于 `MIN_PANE_PX`；
 *   3. 仍不够就再把侧栏压到 `MIN_SIDEBAR_PX`。
 *
 * ⚠️ `dataInWindow` 必须如实传（桌面端为 `false`）：数据页在**吸附窗口**里，不占主窗口宽度。
 * 把它也算进来会导致"开着吸附窗口时 AI 面板被莫名压窄、而且因为判定为'已被压窄'而隐藏拖拽手柄"
 * —— 用户实测反馈："副窗口打开时 AI 助手面板宽度突然回到默认（或更窄），并且无法调节"。
 *
 * 纯函数，便于单测；`Editor/index.tsx` 在渲染时调用它。
 */
export function fitColumnWidths(
  layout: PaneLayout,
  availableWidth: number,
  dataInWindow = true,
): { sidebar: number; pane: number } {
  const w = Number.isFinite(availableWidth) && availableWidth > 0 ? availableWidth : 1440;
  // 只有**窗口内**的面板才吃宽度：桌面端数据页在吸附窗口里，不该算进这里的账
  const panes = (layout.aiOpen ? 1 : 0) + (dataInWindow && layout.dataPane ? 1 : 0);

  /**
   * 侧栏的"最小可用宽度"：
   * - **窄屏（<900px）**跟 editor.css 的媒体查询走 `32vw`（那是既有行为，390px 手机上侧栏约 125px），
   *   此时**不套用** 160 的下限 —— 套了反而会把编辑区挤没；
   * - 宽屏只受 `MIN_SIDEBAR_PX`（160）约束。
   */
  const softSidebarMin = w < 900 ? Math.min(MIN_SIDEBAR_PX, Math.round(w * 0.32)) : MIN_SIDEBAR_PX;

  // ① 侧栏：最多占视口的 32%（与 CSS 同一规则），再夹在"最小可用"之上
  let sidebar = Math.max(softSidebarMin, Math.min(layout.sidebar, Math.round(w * 0.32)));
  let pane = clampPaneWidth(layout.paneWidth);

  const overflowing = (): boolean => sidebar + pane * panes + RAIL_PX + MIN_EDITOR_PX > w;

  if (panes > 0 && overflowing()) {
    // ② 均分"扣掉侧栏、图标栏与编辑区下限"之后剩下的空间，但**只往下压**
    const room = w - sidebar - RAIL_PX - MIN_EDITOR_PX;
    pane = Math.min(pane, clampPaneWidth(Math.floor(room / panes)));
    // ③ 还不够就把侧栏压到"最小可用"（不越过 softSidebarMin）
    if (overflowing()) {
      sidebar = Math.max(softSidebarMin, w - pane * panes - RAIL_PX - MIN_EDITOR_PX);
    }
  }

  return { sidebar: Math.round(sidebar), pane: clampPaneWidth(pane) };
}

export function setPaneWidth(layout: PaneLayout, px: number): PaneLayout {
  const width = clampPaneWidth(px);
  if (layout.paneWidth === width) return layout;
  return { ...layout, paneWidth: width };
}

export function resizePaneBy(layout: PaneLayout, delta: number): PaneLayout {
  return setPaneWidth(layout, layout.paneWidth + delta);
}
